// useTwoPay — composable for the 2Checkout 2Pay.js drop-in client.
//
// Lazily loads the JS library from 2CO's CDN (self-hosting is forbidden by
// PCI scope), constructs a singleton client per merchant code, and exposes
// a typed API for preload / mount / hide / tokenize.
//
// Usage inside a component:
//
//   const { ready, error, component, preload, mount, tokenize } = useTwoPay({
//     lang: 'en',
//     style: asabCardStyle,
//   });
//   onMounted(() => { preload('#card-element'); });
//   // later, on "Subscribe" click:
//   await mount('#card-element');
//   // user fills card + clicks submit:
//   const { token } = await tokenize({ name: 'John Doe', holderNameTime: 3.5 });
//
import { onBeforeUnmount, ref, shallowRef } from 'vue';
import type {
  TwoPayCardStyle,
  TwoPayClientInstance,
  TwoPayComponent,
  TwoPayTokenResponse,
} from '../twopay.d';

const SCRIPT_URL = 'https://2pay-js.2checkout.com/v1/2pay.js';

// Track the script-load promise at module scope so multiple composable
// instances share one <script> tag.
let scriptPromise: Promise<void> | null = null;

function loadTwoPayScript(): Promise<void> {
  if (scriptPromise) return scriptPromise;
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('2pay.js requires a browser environment'));
  }
  if (window.TwoPayClient) {
    scriptPromise = Promise.resolve();
    return scriptPromise;
  }
  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${SCRIPT_URL}"]`,
    );
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () =>
        reject(new Error('Failed to load 2pay.js script')),
      );
      return;
    }
    const s = document.createElement('script');
    s.src = SCRIPT_URL;
    s.async = true;
    s.onload = () => {
      if (!window.TwoPayClient) {
        reject(new Error('2pay.js loaded but TwoPayClient is not defined'));
        return;
      }
      resolve();
    };
    s.onerror = () => reject(new Error('Failed to load 2pay.js script'));
    document.head.appendChild(s);
  });
  return scriptPromise;
}

export interface UseTwoPayOptions {
  merchantCode?: string;
  lang?: string;
  style?: TwoPayCardStyle;
}

export interface TokenizeInput {
  name: string;
  holderNameTime?: number;
  scope?: 'subscription' | 'ordering';
}

export function useTwoPay(opts: UseTwoPayOptions = {}) {
  const merchantCode =
    opts.merchantCode ?? (import.meta.env.VITE_TCO_MERCHANT_CODE as string | undefined) ?? '';

  const ready = ref(false);
  const error = ref<string>('');
  const client = shallowRef<TwoPayClientInstance | null>(null);
  const component = shallowRef<TwoPayComponent | null>(null);

  async function ensureClient(): Promise<TwoPayClientInstance> {
    if (client.value) return client.value;
    if (!merchantCode) {
      throw new Error('TwoPay: missing merchant code (set VITE_TCO_MERCHANT_CODE)');
    }
    await loadTwoPayScript();
    const Ctor = window.TwoPayClient;
    if (!Ctor) throw new Error('TwoPay: TwoPayClient constructor not available');
    const instance = new Ctor(merchantCode);
    if (opts.lang) instance.setup.setLanguage(opts.lang);
    client.value = instance;
    return instance;
  }

  async function ensureComponent(): Promise<TwoPayComponent> {
    if (component.value) return component.value;
    const c = await ensureClient();
    const comp = c.components.create('card', opts.style);
    component.value = comp;
    ready.value = true;
    return comp;
  }

  async function preload(selector: string): Promise<void> {
    try {
      const comp = await ensureComponent();
      comp.preload(selector);
    } catch (e: unknown) {
      error.value = (e as Error).message ?? 'preload failed';
    }
  }

  // mount uses mountAsync + one retry on transient connection errors (per
  // 2Pay.js docs: unmount then re-mountAsync).
  async function mount(selector: string): Promise<void> {
    const comp = await ensureComponent();
    try {
      await comp.mountAsync(selector);
      return;
    } catch {
      comp.unmount();
      try {
        await comp.mountAsync(selector);
      } catch (retry: unknown) {
        error.value = (retry as Error).message ?? 'mount failed';
        throw retry;
      }
    }
  }

  function hide(): void {
    component.value?.hide();
  }

  function unmount(): void {
    component.value?.unmount();
  }

  async function tokenize(input: TokenizeInput): Promise<TwoPayTokenResponse> {
    const c = await ensureClient();
    const comp = await ensureComponent();
    // 2Pay.js expects holderNameTime as a STRING (mirrors the `cardNumberTime`
    // string emitted by the iframe). Sending a number yields
    // 2pay-api-400-15 "Missing Credit Card holder name time.".
    const holderNameTime = (input.holderNameTime ?? 0).toFixed(2);
    return c.tokens.generate(comp, {
      billing: {
        name: input.name,
        holderNameTime,
      },
      scope: input.scope ?? 'subscription',
    });
  }

  onBeforeUnmount(() => {
    try {
      component.value?.unmount();
    } catch {
      /* safe */
    }
  });

  return { ready, error, client, component, preload, mount, hide, unmount, tokenize };
}
